import React, { useState } from "react";
import type { User } from "../types/index";
import {
  presignUpload,
  uploadFileToS3,
  publishSkill,
  GitHubProxyError,
} from "../lib/github-proxy-client";
import FileDropZone from "../components/FileDropZone";
import type { SkillFile } from "../components/FileDropZone";
import { IS_DEV } from "../lib/env";

type SubmitPhase =
  | "idle"
  | "uploading-files"
  | "analyzing-skill"
  | "publishing"
  | "done"
  | "error";

interface SuccessResult {
  contributorName: string;
  submissionDate: string;
  installName: string;
}

export interface EditExistingFile {
  name: string;
  path: string;
  content: string;
}

export interface EditData {
  installKey: string;
  skillName: string;
  description: string;
  keywords: string[];
  existingFiles: EditExistingFile[];
}

interface ContributeSkillPageProps {
  onBack: () => void;
  onViewSkill?: (installName: string) => void;
  user: User | null;
  editData?: EditData;
}

const MAX_NAME = 100;
const MAX_DESC = 2000;
const MAX_KEYWORDS = 50;

function isTextContent(base64: string): boolean {
  try {
    const decoded = atob(base64);
    for (let i = 0; i < Math.min(decoded.length, 512); i++) {
      const code = decoded.charCodeAt(i);
      if (code === 0) return false;
    }
    return true;
  } catch {
    return false;
  }
}

function base64ToFile(name: string, base64: string): File {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], name);
  } catch {
    throw new Error(`Failed to process file "${name}". Please remove and re-upload it.`);
  }
}

export default function ContributeSkillPage({
  onBack,
  onViewSkill,
  user,
  editData,
}: ContributeSkillPageProps): JSX.Element {
  const isEdit = !!editData;

  const [devEmail, setDevEmail] = useState(user?.email ?? "");
  const [devName, setDevName] = useState(user?.name ?? "");

  const showDevIdentity = IS_DEV && !user;
  const effectiveUser: User | null = user
    ?? (devEmail ? { id: "dev", email: devEmail, name: devName || devEmail } : null);

  const [skillName, setSkillName] = useState(editData?.skillName ?? "");
  const [description, setDescription] = useState(editData?.description ?? "");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>(editData?.keywords ?? []);
  const [files, setFiles] = useState<SkillFile[]>([]);

  const [existingFiles, setExistingFiles] = useState<EditExistingFile[]>(
    editData?.existingFiles ?? [],
  );
  const [editedContent, setEditedContent] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of editData?.existingFiles ?? []) {
      if (isTextContent(f.content)) {
        try {
          initial[f.name] = atob(f.content);
        } catch {
          // Corrupted base64 — treat as binary
        }
      }
    }
    return initial;
  });
  const [expandedFile, setExpandedFile] = useState<string | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<SubmitPhase>("idle");
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState<SuccessResult | null>(null);

  if (!user && !IS_DEV) {
    return (
      <div style={{ maxWidth: "600px", margin: "0 auto", textAlign: "center", padding: "2rem 0" }}>
        <h2 style={{ color: "#333", margin: "0 0 0.5rem" }}>Sign in required</h2>
        <p style={{ color: "#666", margin: "0 0 1.5rem" }}>
          You need to be signed in to contribute.
        </p>
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: "#0078d4",
            cursor: "pointer",
            fontSize: "0.9rem",
            padding: 0,
          }}
        >
          &larr; Back to Marketplace
        </button>
      </div>
    );
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!skillName.trim()) e.skillName = "Name is required.";
    else if (skillName.length > MAX_NAME)
      e.skillName = `Name must be ${MAX_NAME} characters or fewer.`;
    if (!description.trim()) e.description = "Description is required.";
    else if (description.length > MAX_DESC)
      e.description = `Description must be ${MAX_DESC} characters or fewer.`;
    if (files.length === 0 && existingFiles.length === 0)
      e.files = "At least one file is required.";
    return e;
  }

  function addKeyword() {
    const tag = keywordInput.trim().toLowerCase();
    if (!tag || keywords.includes(tag) || keywords.length >= MAX_KEYWORDS)
      return;
    setKeywords([...keywords, tag]);
    setKeywordInput("");
  }

  function handleKeywordKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword();
    }
  }

  function removeKeyword(index: number) {
    setKeywords(keywords.filter((_, i) => i !== index));
  }

  function handleFilesAdded(added: SkillFile[]) {
    const addedPaths = new Set(added.map((f) => f.relativePath));

    setExistingFiles((prev) => prev.filter((f) => !addedPaths.has(f.name)));
    setEditedContent((prev) => {
      const next = { ...prev };
      for (const p of addedPaths) delete next[p];
      return next;
    });
    if (expandedFile && addedPaths.has(expandedFile)) {
      setExpandedFile(null);
    }

    const kept = files.filter((f) => !addedPaths.has(f.relativePath));
    setFiles([...kept, ...added]);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.files;
      return next;
    });
  }

  function handleFileRemoved(index: number) {
    setFiles(files.filter((_, i) => i !== index));
  }

  function handleExistingFileRemoved(name: string) {
    setExistingFiles(existingFiles.filter((f) => f.name !== name));
    setEditedContent((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    if (expandedFile === name) setExpandedFile(null);
  }

  function buildFilesForUpload(): SkillFile[] {
    const allFiles: SkillFile[] = [...files];

    for (const ef of existingFiles) {
      const content = editedContent[ef.name];
      let file: File;
      if (content !== undefined) {
        file = new File([content], ef.name);
      } else {
        file = base64ToFile(ef.name, ef.content);
      }
      allFiles.push({ file, relativePath: ef.name });
    }

    return allFiles;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    if (!effectiveUser) return;

    setPhase("uploading-files");
    setSubmitError("");

    try {
      const allFiles = buildFilesForUpload();
      let stagingKeyPrefix: string | undefined;

      for (const sf of allFiles) {
        const contentType = sf.file.type || "application/octet-stream";
        const presign = await presignUpload(
          sf.relativePath,
          contentType,
          stagingKeyPrefix,
        );
        stagingKeyPrefix = presign.stagingKey.substring(
          0,
          presign.stagingKey.indexOf("/", "uploads/".length) + 1,
        );
        await uploadFileToS3(presign.uploadUrl, sf.file);
      }

      setPhase("analyzing-skill");

      await new Promise((r) => setTimeout(r, 500));
      setPhase("publishing");

      const nameChanged = isEdit && skillName.trim() !== editData!.skillName;

      const result = await publishSkill({
        stagingKeyPrefix: stagingKeyPrefix!,
        skillName: skillName.trim(),
        description: description.trim(),
        keywords,
        contributorEmail: effectiveUser.email,
        contributorName: effectiveUser.name ?? effectiveUser.email,
        ...(nameChanged ? { previousInstallKey: editData!.installKey } : {}),
      });

      setSuccess({
        contributorName: effectiveUser.name ?? effectiveUser.email,
        submissionDate: new Date().toLocaleDateString(),
        installName: result.installName,
      });
      setPhase("done");
    } catch (err) {
      setPhase("error");
      if (err instanceof GitHubProxyError) {
        setSubmitError(err.message);
      } else {
        setSubmitError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      }
    }
  }

  if (phase === "done" && success) {
    return (
      <div style={{ maxWidth: "600px", margin: "0 auto" }}>
        <div
          style={{
            backgroundColor: "#e6ffe6",
            border: "1px solid #4caf50",
            borderRadius: "8px",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h2 style={{ color: "#2e7d32", margin: "0 0 1rem" }}>
            {isEdit ? "Updated Successfully" : "Published Successfully"}
          </h2>
          <p style={{ color: "#333", margin: "0 0 0.5rem" }}>
            Contributed by <strong>{success.contributorName}</strong> on{" "}
            {success.submissionDate}
          </p>
          <p style={{ color: "#666", fontSize: "0.9rem", margin: "0 0 1.5rem" }}>
            {isEdit
              ? "Your changes are being processed and will appear in the catalog shortly."
              : "Your submission is being processed and will appear in the catalog shortly."}
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
            {onViewSkill && (
              <button
                onClick={() => onViewSkill(success.installName)}
                style={{
                  padding: "0.5rem 1.5rem",
                  fontSize: "1rem",
                  backgroundColor: "#0078d4",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                View in Catalog
              </button>
            )}
            <button
              onClick={onBack}
              style={{
                background: "none",
                border: "none",
                color: "#0078d4",
                cursor: "pointer",
                fontSize: "0.9rem",
                padding: 0,
              }}
            >
              &larr; Back to Marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isSubmitting = phase !== "idle" && phase !== "error";

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto" }}>
      <button
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: "#0078d4",
          cursor: "pointer",
          fontSize: "0.9rem",
          padding: 0,
          marginBottom: "1rem",
        }}
      >
        &larr; Back to Marketplace
      </button>

      <h2 style={{ margin: "0 0 0.5rem", color: "#333" }}>
        {isEdit ? "Edit Plug-In or Skill" : "Contribute a Plug-In or Skill"}
      </h2>
      <p style={{ margin: "0 0 1.5rem", color: "#666", fontSize: "0.9rem" }}>
        {isEdit
          ? "Update your plug-in or skill's metadata and files."
          : "Share your AI plug-in or skill with the Cox Automotive community. Fill out the form below and upload your files."}
      </p>

      <form onSubmit={handleSubmit}>
        {showDevIdentity && (
          <div style={{
            marginBottom: "1.25rem",
            border: "1px dashed #f0ad4e",
            borderRadius: "8px",
            backgroundColor: "#fdf8e8",
            padding: "0.75rem",
          }}>
            <p style={{ margin: "0 0 0.5rem", fontSize: "0.8rem", color: "#8a6d3b", fontWeight: 600 }}>
              Dev: Contributor identity (required)
            </p>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                type="text"
                value={devName}
                onChange={(e) => setDevName(e.target.value)}
                placeholder="Name"
                style={{
                  flex: 1,
                  padding: "0.5rem",
                  fontSize: "0.85rem",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
              <input
                type="email"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                placeholder="Email (required)"
                style={{
                  flex: 1,
                  padding: "0.5rem",
                  fontSize: "0.85rem",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
            </div>
          </div>
        )}

        {/* Skill Name */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label
            htmlFor="skillName"
            style={{ display: "block", fontWeight: 600, marginBottom: "0.25rem", color: "#333" }}
          >
            Name <span style={{ color: "#cc0000" }}>*</span>
          </label>
          <input
            id="skillName"
            type="text"
            value={skillName}
            onChange={(e) => setSkillName(e.target.value)}
            maxLength={MAX_NAME}
            disabled={isSubmitting}
            placeholder="e.g. PR Review Assistant"
            style={{
              width: "100%",
              padding: "0.75rem",
              fontSize: "1rem",
              border: `1px solid ${errors.skillName ? "#cc0000" : "#ddd"}`,
              borderRadius: "8px",
              boxSizing: "border-box",
            }}
          />
          {errors.skillName && (
            <p style={{ color: "#cc0000", fontSize: "0.85rem", margin: "0.25rem 0 0" }}>
              {errors.skillName}
            </p>
          )}
          <p style={{ color: "#999", fontSize: "0.8rem", margin: "0.25rem 0 0" }}>
            {skillName.length}/{MAX_NAME}
          </p>
        </div>

        {/* Description */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label
            htmlFor="description"
            style={{ display: "block", fontWeight: 600, marginBottom: "0.25rem", color: "#333" }}
          >
            Description <span style={{ color: "#cc0000" }}>*</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={MAX_DESC}
            disabled={isSubmitting}
            rows={3}
            placeholder="Describe what your plug-in or skill does..."
            style={{
              width: "100%",
              padding: "0.75rem",
              fontSize: "1rem",
              border: `1px solid ${errors.description ? "#cc0000" : "#ddd"}`,
              borderRadius: "8px",
              boxSizing: "border-box",
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
          {errors.description && (
            <p style={{ color: "#cc0000", fontSize: "0.85rem", margin: "0.25rem 0 0" }}>
              {errors.description}
            </p>
          )}
          <p style={{ color: "#999", fontSize: "0.8rem", margin: "0.25rem 0 0" }}>
            {description.length}/{MAX_DESC}
          </p>
        </div>

        {/* Keywords */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label
            htmlFor="keywords"
            style={{ display: "block", fontWeight: 600, marginBottom: "0.25rem", color: "#333" }}
          >
            Keywords
          </label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              id="keywords"
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              disabled={isSubmitting}
              placeholder="Type a keyword and press Enter"
              style={{
                flex: 1,
                padding: "0.75rem",
                fontSize: "1rem",
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxSizing: "border-box",
              }}
            />
            <button
              type="button"
              onClick={addKeyword}
              disabled={isSubmitting || !keywordInput.trim()}
              style={{
                padding: "0.75rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: "#f0f0f0",
                color: "#333",
                border: "1px solid #ddd",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Add
            </button>
          </div>
          {keywords.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
              {keywords.map((kw, i) => (
                <span
                  key={kw}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    backgroundColor: "#e6f0ff",
                    color: "#0078d4",
                    padding: "0.25rem 0.5rem",
                    borderRadius: "4px",
                    fontSize: "0.85rem",
                  }}
                >
                  {kw}
                  <button
                    type="button"
                    onClick={() => removeKeyword(i)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#0078d4",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      padding: "0 0.15rem",
                      lineHeight: 1,
                    }}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Existing files (edit mode) */}
        {existingFiles.length > 0 && (
          <div style={{ marginBottom: "1.25rem" }}>
            <label
              style={{ display: "block", fontWeight: 600, marginBottom: "0.25rem", color: "#333" }}
            >
              Existing Files
            </label>
            <div style={{ border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden" }}>
              {existingFiles.map((ef) => {
                const isExpanded = expandedFile === ef.name;
                const isText = isTextContent(ef.content);
                return (
                  <div key={ef.name} style={{ borderBottom: "1px solid #eee" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.5rem 0.75rem",
                        backgroundColor: "#f9f9f9",
                        cursor: isText ? "pointer" : "default",
                      }}
                      onClick={() => {
                        if (isText) setExpandedFile(isExpanded ? null : ef.name);
                      }}
                    >
                      {isText && (
                        <span style={{ marginRight: "0.5rem", fontSize: "0.75rem", color: "#666" }}>
                          {isExpanded ? "▼" : "▶"}
                        </span>
                      )}
                      <span style={{ flex: 1, fontSize: "0.9rem", color: "#333" }}>
                        {ef.name}
                      </span>
                      {!isText && (
                        <span style={{ fontSize: "0.8rem", color: "#999", marginRight: "0.5rem" }}>
                          Binary file
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          handleExistingFileRemoved(ef.name);
                        }}
                        disabled={isSubmitting}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#cc0000",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          padding: "0.25rem 0.5rem",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    {isExpanded && isText && (
                      <textarea
                        value={editedContent[ef.name] ?? ""}
                        onChange={(ev) =>
                          setEditedContent((prev) => ({ ...prev, [ef.name]: ev.target.value }))
                        }
                        disabled={isSubmitting}
                        style={{
                          width: "100%",
                          minHeight: "120px",
                          padding: "0.5rem 0.75rem",
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          border: "none",
                          borderTop: "1px solid #eee",
                          boxSizing: "border-box",
                          resize: "vertical",
                          backgroundColor: "#fafafa",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* New files */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{ display: "block", fontWeight: 600, marginBottom: "0.25rem", color: "#333" }}
          >
            {isEdit ? "Add New Files" : "Files"}{" "}
            {!isEdit && <span style={{ color: "#cc0000" }}>*</span>}
          </label>
          <FileDropZone
            files={files}
            onFilesAdded={handleFilesAdded}
            onFileRemoved={handleFileRemoved}
          />
          {errors.files && (
            <p style={{ color: "#cc0000", fontSize: "0.85rem", margin: "0.25rem 0 0" }}>
              {errors.files}
            </p>
          )}
        </div>

        {/* Progress indicator */}
        {isSubmitting && (
          <div
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              padding: "1rem",
              marginBottom: "1rem",
            }}
          >
            <ProgressStep label="Uploading files" active={phase === "uploading-files"} done={phase !== "uploading-files"} />
            <ProgressStep label="Analyzing" active={phase === "analyzing-skill"} done={phase === "publishing"} />
            <ProgressStep label={isEdit ? "Updating" : "Publishing to marketplace"} active={phase === "publishing"} done={false} />
          </div>
        )}

        {/* Error state */}
        {phase === "error" && submitError && (
          <div
            style={{
              backgroundColor: "#fff0f0",
              border: "1px solid #cc0000",
              borderRadius: "8px",
              padding: "1rem",
              marginBottom: "1rem",
            }}
          >
            <p style={{ color: "#cc0000", margin: "0 0 0.5rem", fontWeight: 600 }}>
              {isEdit ? "Update failed" : "Upload failed"}
            </p>
            <p style={{ color: "#333", margin: "0 0 0.75rem", fontSize: "0.9rem" }}>
              {submitError}
            </p>
            <button
              type="submit"
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: "#0078d4",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Submit */}
        {phase === "idle" && (
          <button
            type="submit"
            style={{
              width: "100%",
              padding: "0.75rem",
              fontSize: "1rem",
              fontWeight: 600,
              backgroundColor: "#0078d4",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            {isEdit ? "Update" : "Submit"}
          </button>
        )}
      </form>
    </div>
  );
}

function ProgressStep({
  label,
  active,
  done,
}: {
  label: string;
  active: boolean;
  done: boolean;
}): JSX.Element {
  const color = done ? "#4caf50" : active ? "#0078d4" : "#999";
  const icon = done ? "✓" : active ? "●" : "○";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        padding: "0.25rem 0",
        color,
        fontWeight: active ? 600 : 400,
      }}
    >
      <span style={{ fontSize: "0.9rem" }}>{icon}</span>
      <span style={{ fontSize: "0.9rem" }}>{label}</span>
    </div>
  );
}
